import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  SheetMappingItem,
  SheetMappingRequest,
  SheetMappingListResponse,
  SheetMappingDetailResponse,
  SheetMappingMutationResponse,
  SheetMappingDeleteResponse,
  ColumnMappingItem,
  ColumnMappingRequest,
  ColumnMappingListResponse,
  ColumnMappingMutationResponse,
  ColumnMappingDeleteResponse,
} from '../types/import-configuration-types';
import { ApplicationType } from '../types/translation-types';

// Map string application type names to enum values
const ApplicationTypeStringToEnum: Record<string, ApplicationType> = {
  'EvHomeFlutter': ApplicationType.EvHomeFlutter,
  'EnergyAndroidCharger': ApplicationType.EnergyAndroidCharger,
  'EnergyAndroidKiosk': ApplicationType.EnergyAndroidKiosk,
  'WeezmoWeb': ApplicationType.WeezmoWeb,
  'WeezmoPdf': ApplicationType.WeezmoPdf,
  'FlutterWebApp': ApplicationType.FlutterWebApp,
  'ReactWeb': ApplicationType.ReactWeb,
};

/**
 * Transform API sheet mapping response to convert string applicationType to enum
 */
function transformSheetMapping(apiItem: Record<string, unknown>): SheetMappingItem {
  const appTypeValue = apiItem.applicationType;
  let applicationType: ApplicationType;

  if (typeof appTypeValue === 'string') {
    applicationType = ApplicationTypeStringToEnum[appTypeValue] ?? ApplicationType.EvHomeFlutter;
  } else if (typeof appTypeValue === 'number') {
    applicationType = appTypeValue as ApplicationType;
  } else {
    applicationType = ApplicationType.EvHomeFlutter;
  }

  return {
    mappingId: apiItem.mappingId as number,
    sheetName: apiItem.sheetName as string,
    applicationType,
    keyColumnNumber: apiItem.keyColumnNumber as number,
    descriptionColumnNumber: apiItem.descriptionColumnNumber as number | undefined,
    isActive: apiItem.isActive as boolean,
    sortOrder: apiItem.sortOrder as number,
    columnMappings: (apiItem.columnMappings as ColumnMappingItem[]) ?? [],
  };
}

/**
 * Import Configuration API
 * Handles all API calls for managing Excel import configuration
 */
export class ImportConfigurationApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      console.error('ImportConfigurationApi: No operator selected, preventing API call');
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.tokenManager.getAuthHeaders();

    return {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': operatorId.toString(),
    };
  }

  // ============================================================================
  // Sheet Mappings
  // ============================================================================

  /**
   * Get all sheet mappings
   * API: GET /api/v1/import-configuration/sheet-mappings
   */
  async getSheetMappings(): Promise<SheetMappingListResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/import-configuration/sheet-mappings`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Import Configuration API',
      '/import-configuration/sheet-mappings'
    );

    const data = await response.json();

    // Handle response formats - API returns "rows" array
    let rawMappings: Record<string, unknown>[] = [];

    if (data.rows !== undefined) {
      rawMappings = data.rows;
    } else if (data.mappings !== undefined) {
      rawMappings = data.mappings;
    } else if (Array.isArray(data)) {
      rawMappings = data;
    } else {
      return data;
    }

    // Transform each mapping to convert string applicationType to enum
    const mappings = rawMappings.map(transformSheetMapping);

    return {
      isSuccess: true,
      mappings,
    };
  }

  /**
   * Get a single sheet mapping with its column mappings
   * API: GET /api/v1/import-configuration/sheet-mappings/{id}
   */
  async getSheetMappingById(mappingId: number): Promise<SheetMappingDetailResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/import-configuration/sheet-mappings/${mappingId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Import Configuration API',
      `/import-configuration/sheet-mappings/${mappingId}`
    );

    const data = await response.json();

    // Handle wrapped response
    if (data.mapping) {
      return {
        isSuccess: true,
        mapping: transformSheetMapping(data.mapping),
      };
    }

    // Handle direct mapping object
    if (data.mappingId !== undefined) {
      return {
        isSuccess: true,
        mapping: transformSheetMapping(data),
      };
    }

    return data;
  }

  /**
   * Create or update a sheet mapping
   * API: PUT /api/v1/import-configuration/sheet-mappings
   */
  async saveSheetMapping(request: SheetMappingRequest): Promise<SheetMappingMutationResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/import-configuration/sheet-mappings`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(request),
      },
      'Import Configuration API',
      '/import-configuration/sheet-mappings'
    );

    const result = await response.json();

    // Check for API-level validation errors
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to save sheet mapping');
    }

    // Handle wrapped response
    if (result.mapping) {
      return {
        isSuccess: true,
        mapping: transformSheetMapping(result.mapping),
      };
    }

    // Handle direct mapping object
    if (result.mappingId !== undefined) {
      return {
        isSuccess: true,
        mapping: transformSheetMapping(result),
      };
    }

    return result;
  }

  /**
   * Delete a sheet mapping
   * API: DELETE /api/v1/import-configuration/sheet-mappings/{id}
   */
  async deleteSheetMapping(mappingId: number): Promise<SheetMappingDeleteResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/import-configuration/sheet-mappings/${mappingId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'DELETE',
        headers,
      },
      'Import Configuration API',
      `/import-configuration/sheet-mappings/${mappingId}`
    );

    const result = await response.json();
    return result;
  }

  // ============================================================================
  // Column Mappings
  // ============================================================================

  /**
   * Get column mappings for a sheet
   * API: GET /api/v1/import-configuration/sheet-mappings/{sheetId}/columns
   */
  async getColumnMappings(sheetMappingId: number): Promise<ColumnMappingListResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/import-configuration/sheet-mappings/${sheetMappingId}/columns`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Import Configuration API',
      `/import-configuration/sheet-mappings/${sheetMappingId}/columns`
    );

    const data = await response.json();

    // Handle response formats
    if (data.columns !== undefined) {
      return {
        isSuccess: true,
        columns: data.columns,
      };
    }

    if (Array.isArray(data)) {
      return {
        isSuccess: true,
        columns: data,
      };
    }

    return data;
  }

  /**
   * Create or update a column mapping
   * API: PUT /api/v1/import-configuration/sheet-mappings/{sheetId}/columns
   */
  async saveColumnMapping(request: ColumnMappingRequest): Promise<ColumnMappingMutationResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/import-configuration/sheet-mappings/${request.sheetMappingId}/columns`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(request),
      },
      'Import Configuration API',
      `/import-configuration/sheet-mappings/${request.sheetMappingId}/columns`
    );

    const result = await response.json();

    // Check for API-level validation errors
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to save column mapping');
    }

    // Handle wrapped response - API returns "mapping" property for column mappings
    if (result.mapping) {
      return {
        isSuccess: true,
        column: result.mapping as ColumnMappingItem,
      };
    }

    // Handle legacy "column" property if API changes
    if (result.column) {
      return {
        isSuccess: true,
        column: result.column,
      };
    }

    // Handle direct column object
    if (result.columnMappingId !== undefined) {
      return {
        isSuccess: true,
        column: result as ColumnMappingItem,
      };
    }

    return result;
  }

  /**
   * Delete a column mapping
   * API: DELETE /api/v1/import-configuration/columns/{columnId}
   */
  async deleteColumnMapping(columnId: number): Promise<ColumnMappingDeleteResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/import-configuration/columns/${columnId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'DELETE',
        headers,
      },
      'Import Configuration API',
      `/import-configuration/columns/${columnId}`
    );

    const result = await response.json();
    return result;
  }
}
